import React, { Children, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import ReactMapboxGl, { MapContext, ZoomControl, ScaleControl, RotationControl, Popup, Marker, Source, Layer } from 'react-mapbox-gl';
import DrawControl from 'react-mapbox-gl-draw';

import KoalaPopup from '../Popups/KoalaPopup';
import BoundaryPopup from '../Popups/BoundaryPopup';

import { geolocateUser, koalaItemClick, koalaPopupClose, boundaryItemClick, boundaryPopupClose, hideSearchMarker } from '../../actions/mainview/mainview';
import { mainviewActions, modalActions } from '../../helpers/constants';

const Map = ReactMapboxGl({
  accessToken: process.env.REACT_APP_Mapbox_API_Key,
  preserveDrawingBuffer: true,
  attributionControl: false,
  pitchWithRotate: true,
  renderWorldCopies: false,
  transformRequest: url => {
    if(url.includes('/sightings/')) {
      const jwt = JSON.parse(localStorage.getItem('User_Auth_Tokens'))?.token;

      if (!jwt) return false;

      return {
        url: url,
        headers: { 'Authorization': `JWT ${ jwt }` }
      }
    }
  }
});

const Mainview = props => {

  const dispatch = useDispatch(),
        appProps = useSelector(store => store.app),
        mainviewProps = useSelector(store => store.mainview),
        [mapCenter, setMapCenter] = useState([mainviewProps.longitude, mainviewProps.latitude]),
        [mapZoom, setMapZoom] = useState([mainviewProps.zoom]),
        [mapPitch, setMapPitch] = useState([mainviewProps.pitch]),
        [mapBearing, setMapBearing] = useState([mainviewProps.bearing]),
        [isMobile, setIsMobile] = useState(false),
        isDrawingFlag = useRef(false),
        currentActiveDraws = useRef(0);
           
  const _initMap = map => props.setMap(map);

  const _addSources = map => {
    if (map.getSource('sightings') === undefined) {
      map.addSource('sightings', {
        type: 'vector',
        tiles: [ `${ process.env.REACT_APP_ROOT_TILES }/api/data/sightings/tiles/{z}/{x}/{y}.mvt` ]
      });
    }
    
    if (map.getSource('kha-raster-tiles') === undefined) {
      map.addSource('kha-raster-tiles', {
        type: 'raster',
        tiles: [ `${ process.env.REACT_APP_ROOT_TILES }/tiles/raster/khas/{z}/{x}/{y}.png` ]
      });
    }

    if (map.getSource('nvis-raster-tiles') === undefined) {
      map.addSource('nvis-raster-tiles', {
        type: 'raster',
        tiles: [ `${ process.env.REACT_APP_ROOT_TILES }/tiles/raster/nvis/{z}/{x}/{y}.png` ]
      });
    }

    if (map.getSource('preclearing-raster-tiles') === undefined) {
      map.addSource('preclearing-raster-tiles', {
        type: 'raster',
        tiles: [ `${ process.env.REACT_APP_ROOT_TILES }/tiles/raster/khapreclrs/{z}/{x}/{y}.png` ]
      });
    }
  }

  const _addImages = map => {
    if (!map.hasImage('koala-healthy')) {
      map.loadImage(`${ process.env.REACT_APP_ROOT_TILES }/media/KHH.png`, (error, image) => {
        if (map.hasImage('koala-healthy')) return false;
        if (!error) map.addImage('koala-healthy', image);
      });
    }
    
    if (!map.hasImage('koala-dead')) {
      map.loadImage(`${ process.env.REACT_APP_ROOT_TILES }/media/KHD.png`, (error, image) => {
        if (map.hasImage('koala-dead')) return false;
        if (!error) map.addImage('koala-dead', image);
      });
    }
    
    if (!map.hasImage('koala-sick')) {
      map.loadImage(`${ process.env.REACT_APP_ROOT_TILES }/media/KHS.png`, (error, image) => {
        if (map.hasImage('koala-sick')) return false;
        if (!error) map.addImage('koala-sick', image);
      });
    }
    
    if (!map.hasImage('koala-unknown')) {
      map.loadImage(`${ process.env.REACT_APP_ROOT_TILES }/media/KHU.png`, (error, image) => {
        if (map.hasImage('koala-unknown')) return false;
        if (!error) map.addImage('koala-unknown', image);
      });
    }
  }

  useEffect(() => setIsMobile(window.innerWidth <= 850), []);
  useEffect(() => dispatch(geolocateUser()), [dispatch]);
  useEffect(() => setMapZoom([mainviewProps.zoom]), [mainviewProps.zoom]);
  useEffect(() => setMapPitch([mainviewProps.pitch]), [mainviewProps.pitch]);
  useEffect(() => setMapBearing([mainviewProps.bearing]), [mainviewProps.bearing]);
  useEffect(() => setMapCenter([mainviewProps.longitude, mainviewProps.latitude]), [mainviewProps.latitude, mainviewProps.longitude]);

  useEffect(() => {
    if (!props.map) return;

    // solution to refreshing specific tileset found here: https://github.com/mapbox/mapbox-gl-js/issues/2941#issuecomment-518631078
    props.map.getSource('sightings').tiles = [ `${ process.env.REACT_APP_ROOT_TILES }/api/data/sightings/tiles/{z}/{x}/{y}.mvt?dt=${ Date.now() }` ];
    props.map.style.sourceCaches['sightings'].clearTiles();
    props.map.style.sourceCaches['sightings'].update(props.map.transform);
    props.map.triggerRepaint();

  }, [mainviewProps.editedKoalaSighting, appProps.isUserAuthenticated, mainviewProps.addedKoalaId]);

  const _featureClick = e => {

    if (isDrawingFlag.current || currentActiveDraws.current > 0) { // Don't show popup while user is in draw mode
      e.preventDefault();
      return false;
    }

    const sightingFeature = e.features.find(x => x.layer.id === 'sightings_layer');

    if (sightingFeature) {
      dispatch(koalaItemClick(true, {
        ...sightingFeature.properties,
        coords: sightingFeature.geometry.coordinates
      }));
    }
    else {
      const features = props.map.queryRenderedFeatures(e.point);
      const sighting = features.find(x => x.layer.id === 'sightings_layer');

      if (sighting) return false;

      dispatch(boundaryPopupClose()); // Close popup to reset it ready for new coords
      dispatch(boundaryItemClick(true, e.lngLat));
    }
    
  }

  return (
    <main className="mainview">
      
      {
        Children.map(props.children, child => {
          return child
        })
      }

      <Map
        className="map"
        containerStyle={{ height: "100vh", width: '100%' }}
        center={ mapCenter }
        zoom={ mapZoom }
        pitch={ mapPitch }
        bearing={ mapBearing }
        style={ mainviewProps.mapStyle }
        movingMethod="easeTo"
        easeToOptions={ { speed: 2 } }
        onStyleLoad={ map => _initMap(map) }
        onStyleData={ map => _addSources(map) }
        maxBounds={ mainviewProps.maxBounds }>

        <Source id="adminbndytiles" tileJsonSource={{
          "type": "vector",
          "url": `${ process.env.REACT_APP_ROOT_TILES }/tiles/adminbndys/tile.json`
        }} />

        <Source id="cadtiles" tileJsonSource={{
          "type": "vector",
          "url": `${ process.env.REACT_APP_ROOT_TILES }/tiles/cads/tile.json`
        }} />
        
        <Source id="nvis-vector-tiles" tileJsonSource={{
          type: 'vector',
          url: `${ process.env.REACT_APP_ROOT_TILES }/tiles/nvis/tile.json`
        }} />

        <Source id="kha-vector-tiles" tileJsonSource={{
          type: 'vector',
          url: `${ process.env.REACT_APP_ROOT_TILES }/tiles/khas/tile.json`
        }} />

        <Source id="preclearing-vector-tiles" tileJsonSource={{
          type: 'vector',
          url: `${ process.env.REACT_APP_ROOT_TILES }/tiles/khapreclrs/tile.json`
        }} />

        {
          !isMobile &&
          <DrawControl
            controls={{
              combine_features: false,
              uncombine_features: false
            }}
            clickBuffer={ 5 }
            styles={
              [
                {
                  'id': 'highlight-active-points',
                  'type': 'circle',
                  'filter': ['all', ['==', '$type', 'Point'],
                    ['==', 'meta', 'feature'],
                    ['==', 'active', 'true']
                  ],
                  'paint': {
                    'circle-radius': 5,
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 1.5,
                    'circle-color': '#0bdce3'
                  }
                },
                {
                  'id': 'points-are-red',
                  'type': 'circle',
                  'filter': ['all', ['==', '$type', 'Point'],
                    ['==', 'meta', 'feature'],
                    ['==', 'active', 'false']
                  ],
                  'paint': {
                    'circle-radius': 5,
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 1.5,
                    'circle-color': '#ff0000'
                  }
                },
                // ACTIVE (being drawn)
                // line stroke
                {
                  "id": "gl-draw-line",
                  "type": "line",
                  "filter": ["all", ["==", "$type", "LineString"],
                    ["==", "active", "true"]
                  ],
                  "layout": {
                    "line-cap": "round",
                    "line-join": "round"
                  },
                  "paint": {
                    "line-color": "#0bdce3",
                    "line-dasharray": [0.2, 2],
                    "line-width": 2.5
                  }
                },
                // polygon fill
                {
                  "id": "gl-draw-polygon-fill",
                  "type": "fill",
                  "filter": ["all", ["==", "$type", "Polygon"],
                    ["==", "active", "true"]
                  ],
                  "paint": {
                    "fill-color": "#0bdce3",
                    "fill-outline-color": "#0bdce3",
                    "fill-opacity": 0.1
                  }
                },
                // polygon outline stroke
                {
                  "id": "gl-draw-polygon-stroke-active",
                  "type": "line",
                  "filter": ["all", ["==", "$type", "Polygon"],
                    ["==", "active", "true"]
                  ],
                  "layout": {
                    "line-cap": "round",
                    "line-join": "round"
                  },
                  "paint": {
                    "line-color": "#0bdce3",
                    "line-dasharray": [0.2, 2],
                    "line-width": 2.5
                  }
                },
                // vertex point halos
                {
                  "id": "gl-draw-polygon-and-line-vertex-halo-active",
                  "type": "circle",
                  "filter": ["all", ["==", "meta", "vertex"],
                    ["==", "$type", "Point"],
                    ["!=", "mode", "static"]
                  ],
                  "paint": {
                    "circle-radius": 6,
                    "circle-color": "#FFF"
                  }
                },
                // vertex points
                {
                  "id": "gl-draw-polygon-and-line-vertex-active",
                  "type": "circle",
                  "filter": ["all", ["==", "meta", "vertex"],
                    ["==", "$type", "Point"],
                    ["!=", "mode", "static"]
                  ],
                  "paint": {
                    "circle-radius": 4,
                    "circle-color": "#0bdce3",
                  }
                },
                // INACTIVE (static, already drawn)
                // line stroke
                {
                  "id": "gl-draw-line-static",
                  "type": "line",
                  "filter": ["all", ["==", "$type", "LineString"],
                    ["==", "active", "false"]
                  ],
                  "layout": {
                    "line-cap": "round",
                    "line-join": "round"
                  },
                  "paint": {
                    "line-color": "#ff0000",
                    "line-width": 2.5
                  }
                },
                // polygon fill
                {
                  "id": "gl-draw-polygon-fill-static",
                  "type": "fill",
                  "filter": ["all", ["==", "$type", "Polygon"],
                    ["==", "active", "false"]
                  ],
                  "paint": {
                    "fill-color": "#ff0000",
                    "fill-outline-color": "#ff0000",
                    "fill-opacity": 0.1
                  }
                },
                // polygon outline
                {
                  "id": "gl-draw-polygon-stroke-static",
                  "type": "line",
                  "filter": ["all", ["==", "$type", "Polygon"],
                    ["==", "active", "false"]
                  ],
                  "layout": {
                    "line-cap": "round",
                    "line-join": "round"
                  },
                  "paint": {
                    "line-color": "#ff0000",
                    "line-width": 2.5
                  }
                }
              ]
            }
            onDrawModeChange={ e => isDrawingFlag.current = e.mode !== 'simple_select' }
            onDrawSelectionChange={ e => currentActiveDraws.current = e.features.length }
            onDrawDelete={ () => { isDrawingFlag.current = false; currentActiveDraws.current = 0 } }
            position="top-right" />
        }
        
        <ZoomControl className="map__control map__control--zoom" />
        <RotationControl className="map__control map__control--rotation" />
        <ScaleControl position="bottom-right" className="scaleControl" />

        {
          mainviewProps.showSearchMarker &&
          <Marker coordinates={ mainviewProps.selectedAddress.center }>
            <div className="map__marker map__marker--red" title="Click to remove marker" onClick={ () => { dispatch(hideSearchMarker()) } }></div>
          </Marker>
        }

        {
          mainviewProps.showKoalaPopup &&
          <Popup coordinates={ mainviewProps.koalaPopupData.coords } offset={ 10 }>
            <span className="map__popup-close" onClick={ () => dispatch(koalaPopupClose()) } title="Close sighting popup">&times;</span>
            <KoalaPopup { ...mainviewProps.koalaPopupData } />
          </Popup>
        }
        {
          mainviewProps.showBoundaryPopup &&
          <Popup coordinates={ mainviewProps.boundaryCoords }>
            <span className="map__popup-close" onClick={ () => dispatch(boundaryPopupClose()) } title="Close boundary popup">&times;</span>
            <BoundaryPopup />
          </Popup>
        }

        <Layer
          id="sightings_layer"
          type="symbol"
          sourceId="sightings"
          sourceLayer="default"
          onClick={ e => _featureClick(e) }
          onMouseEnter={ e => e.target.getCanvasContainer().style.cursor = 'pointer' }
          onMouseLeave={ e => e.target.getCanvasContainer().style.cursor = 'grab' }
          layout={{
            'visibility': mainviewProps.showSightingLayer ? 'visible' : 'none',
            //'icon-allow-overlap': true,
            //"icon-size": .5,
            'icon-image': [
              'match',
              ['get', 'status'],
              'Healthy',
              'koala-healthy',
              'Deceased',
              'koala-dead',
              'Sick/Injured',
              'koala-sick',
              'koala-unknown'
            ]
          }} />

        <Layer
          id="cad_layers"
          type="line"
          sourceId="cadtiles"
          sourceLayer="cads"
          layout={{
            'visibility': mainviewProps.showPropertyLayer ? 'visible' : 'none'
          }}
          paint={{
            'line-width': 1.,
            'line-color': '#CC5500'
          }} />

        <Layer
          id="state_boundary"
          type="fill"
          sourceId="adminbndytiles"
          sourceLayer="states"
          paint={{
            'fill-color': 'rgba(0,0,0,0)',
            'fill-opacity': 1,
            'fill-outline-color': 'brown',
            'fill-antialias': true
          }}
          layout={{
            'visibility': mainviewProps.showStateLayer ? 'visible' : 'none'
          }}
          onClick={ e => _featureClick(e) } />
        <Layer
          id="state_boundary_label"
          type="symbol"
          sourceId="adminbndytiles"
          sourceLayer="states"
          layout={{
            'text-field': '{STATE_ELECTORAL_NAME}',
            'text-anchor': 'center',
            'text-size': 12,
            'visibility': mainviewProps.showStateLayer ? 'visible' : 'none'
          }}
          paint={{
            'text-color': 'brown',
            'text-halo-blur': .99,
            'text-halo-width': .5,
            'text-halo-color': '#fff'
          }} />

        <Layer
          id="federal_boundary"
          type="fill"
          sourceId="adminbndytiles"
          sourceLayer="federals"
          paint={{
            'fill-color': 'rgba(0,0,0,0)',
            'fill-opacity': 1,
            'fill-outline-color': 'blue',
            'fill-antialias': true
          }}
          layout={{
            'visibility': mainviewProps.showFederalLayer ? 'visible' : 'none'
          }}
          onClick={ e => _featureClick(e) } />
        <Layer
          id="federal_boundary_label"
          type="symbol"
          sourceId="adminbndytiles"
          sourceLayer="federals"
          layout={{
            'text-field': '{COMM_ELECTORAL_NAME}',
            'text-anchor': 'center',
            'text-size': 12,
            'visibility': mainviewProps.showFederalLayer ? 'visible' : 'none'
          }}
          paint={{
            'text-color': 'blue',
            'text-halo-blur': .99,
            'text-halo-width': .5,
            'text-halo-color': '#fff'
          }} />

        <Layer
          id="lga_boundary"
          type="fill"
          sourceId="adminbndytiles"
          sourceLayer="lgas"
          paint={{
            'fill-color': 'rgba(0,0,0,0)',
            'fill-opacity': 1,
            'fill-outline-color': 'purple',
            'fill-antialias': true
          }}
          layout={{
            'visibility': mainviewProps.showLGALayer ? 'visible' : 'none'
          }}
          onClick={ e => _featureClick(e) } />
        <Layer
          id="lga_boundary_label"
          type="symbol"
          sourceId="adminbndytiles"
          sourceLayer="lgas"
          layout={{
            'text-field': '{LGA_NAME}',
            'text-anchor': 'center',
            'text-size': 12,
            'visibility': mainviewProps.showLGALayer ? 'visible' : 'none'
          }}
          paint={{
            'text-color': 'purple',
            'text-halo-blur': .99,
            'text-halo-width': .5,
            'text-halo-color': '#fff'
          }} />

        <Layer
          id="nvis_raster_tiles"
          type="raster"
          maxZoom={ 12 }
          sourceId="nvis-raster-tiles"
          layout={{
            'visibility': mainviewProps.showNVISHabitat ? 'visible' : 'none'
          }}
          paint={{ 'raster-opacity': 0.6 }} />
        <Layer
          id="nvis_vector_tiles"
          type="fill"
          sourceId="nvis-vector-tiles"
          sourceLayer="nvis"
          layout={{
            'visibility': mainviewProps.showNVISHabitat ? 'visible' : 'none'
          }}
          paint={{
            'fill-color': '#74b374',
            'fill-opacity': 0.6,
            'fill-antialias': false
          }} />

        <Layer
          id="kha_raster_tiles"
          type="raster"
          maxZoom={ 12 }
          sourceId="kha-raster-tiles"
          layout={{
            'visibility': mainviewProps.showKHAHabitat ? 'visible' : 'none'
          }}
          paint={{ 'raster-opacity': 0.6 }} />
        <Layer
          id="kha_vector_tiles"
          type="fill"
          sourceLayer="khas"
          sourceId="kha-vector-tiles"
          layout={{
            'visibility': mainviewProps.showKHAHabitat ? 'visible' : 'none'
          }}
          paint={{
            'fill-color': ["match", ["get", "Koala_Habitat_Class"],
                "Primary Habitat", "#f51919",
                "Other vegetation", "#fdd960",
                "Secondary Habitat (Class A)","#079d51",
                "Secondary Habitat (Class B)","#74b374",
                "Secondary Habitat (Class C)","#c3f5b5",
                "Unknown Habitat Quality","#f1abf9",
                "rgba(255, 255, 255, 0.0)"
              ],
            'fill-antialias': false,
            'fill-opacity': 0.6
          }} />

        <Layer
          id="preclearing_raster_tiles"
          type="raster"
          maxZoom={ 12 }
          sourceId="preclearing-raster-tiles"
          layout={{
            'visibility': mainviewProps.showPreClearingHabitat ? 'visible' : 'none'
          }}
          paint={{ 'raster-opacity': 0.6 }} />
        <Layer
          id="preclear_vector_tiles"
          type="fill"
          sourceId="preclearing-vector-tiles"
          sourceLayer="khapreclrs"
          layout={{
            'visibility': mainviewProps.showPreClearingHabitat ? 'visible' : 'none'
          }}
          paint={{
            'fill-color': ['match', ['get', 'Koala_Habitat_Class'],
              'Primary Habitat', '#F60000',
              'Other vegetation', '#FED761',
              "Secondary Habitat (Class A)", "#07CD67",
              "Secondary Habitat (Class B)", "#55FDA8",
              "Secondary Habitat (Class C)", "#A4FDD1",
              "Unknown Habitat Quality", "#F700FE",
              'rgba(255, 255, 255, 0)'],
            'fill-opacity': 0.6,
            'fill-antialias': false
          }} />

          <div className="map__control map__control--reset" onClick={ () => dispatch({ type: mainviewActions.RESET_MAP_VIEW }) } title="Click to reset map view">
            <i className="fas fa-globe-asia"></i>
          </div>

        <MapContext.Consumer>
          {
            map => {
              _addImages(map);
              map.moveLayer('sightings_layer');

              map.on('style.load', () => {
                _addImages(map)
                map.moveLayer('sightings_layer');
              });

              map.on('click', e => {
                if (mainviewProps.isChoosingLocation) { // Don't care about normal clicks, only when user is selecting sighting location we want to listen
                  dispatch({
                    type: mainviewActions.SELECT_LOCATION_ON_MAP_COMPLETE,
                    payload: e.lngLat
                  });
                  dispatch({
                    type: modalActions.SUSPEND_MODAL,
                    payload: false
                  });
                }
              });
            }
          }
        </MapContext.Consumer>
      </Map>

    </main>
  );

}

export default Mainview;